import React, { useCallback, useEffect, useRef } from "react";
import {
  Box,
  BoxModel,
  DefaultFontStyle,
  Editor,
  FileHelpers,
  getPointerInfo,
  preventDefault,
  stopEventPropagation,
  SvgExportDef,
  TLDefaultHorizontalAlignStyle,
  tlenv,
  TLShape,
  TLShapeId,
  TLUnknownShape,
  useValue,
  Vec,
  VecModel,
} from "@tldraw/tldraw";
import { INDENT, TextHelpers } from "./TextHelper";
import { CustomTextUtilType } from "./CustomTextUtil";

export function useEditableText(shapeId: TLShapeId, type: string, text: string, editor: Editor, isSelected: boolean) {
  const rInput = useRef<HTMLTextAreaElement>(null);
  const isEditing = useValue("isEditing", () => editor.getEditingShapeId() === shapeId, [editor, isSelected]);
  const isEditingAnything = useValue("isEditingAnything", () => !!editor.getEditingShapeId(), [
    editor,
    shapeId,
    isSelected,
  ]);

  useEffect(() => {
    function selectAllIfEditing(event: { shapeId: TLShapeId }) {
      if (event.shapeId === shapeId) {
        rInput.current?.select();
      }
    }

    editor.on("select-all-text", selectAllIfEditing);
    return () => {
      editor.off("select-all-text", selectAllIfEditing);
    };
  }, [editor, shapeId, isEditing]);

  useEffect(() => {
    if (!isEditing) return;

    if (document.activeElement !== rInput.current) {
      rInput.current?.focus();
    }

    if (editor.getInstanceState().isCoarsePointer) {
      rInput.current?.select();
    }

    // XXX(mime): This fixes iOS not showing the cursor sometimes.
    // This "shakes" the cursor awake.
    if (tlenv.isSafari) {
      rInput.current?.blur();
      rInput.current?.focus();
    }
  }, [editor, isEditing]);

  // When the user presses ctrl / meta enter, complete the editing state.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (editor.getEditingShapeId() !== shapeId) return;

      switch (e.key) {
        case "Enter": {
          if (e.ctrlKey || e.metaKey) {
            editor.complete();
          }
          break;
        }
      }
    },
    [editor, shapeId]
  );

  // When the text changes, update the text value.
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (editor.getEditingShapeId() !== shapeId) return;

      let text = TextHelpers.normalizeText(e.currentTarget.value);

      // ------- Bug fix ------------
      // Replace tabs with spaces when pasting
      const untabbedText = text.replace(/\t/g, INDENT);
      if (untabbedText !== text) {
        const selectionStart = e.currentTarget.selectionStart;
        e.currentTarget.value = untabbedText;
        e.currentTarget.selectionStart = selectionStart + (untabbedText.length - text.length);
        e.currentTarget.selectionEnd = selectionStart + (untabbedText.length - text.length);
        text = untabbedText;
      }
      // ----------------------------

      editor.updateShape<TLUnknownShape & { props: { text: string } }>({
        id: shapeId,
        type,
        props: { text },
      });
    },
    [editor, shapeId, type]
  );

  const handleInputPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // N.B. We used to only do this only when isEditing to help
      // prevent an issue where you could drag a selected shape
      // behind another shape. That is addressed now by the CSS logic
      // looking at data-isselectinganything.
      //
      // We still need to follow this logic even if not isEditing
      // because otherwise there is some flakiness in selection.
      // When selecting text, it would sometimes select some text
      // partially if we didn't dispatch/stop below.

      try {
        const shape = editor.getShape(shapeId);
        if (!shape) return;

        const pointerInfo = getPointerInfo(e);

        // Validate that pointerInfo has the required properties before dispatching
        if (!pointerInfo || !pointerInfo.point) {
          console.warn("Invalid pointer info, skipping dispatch");
          return;
        }

        editor.dispatch({
          ...pointerInfo,
          type: "pointer",
          name: "pointer_down",
          target: "shape",
          shape,
        });
      } catch (error) {
        console.warn("Error in handleInputPointerDown:", error);
      }

      stopEventPropagation(e); // we need to prevent blurring the input
    },
    [editor, shapeId]
  );

  const noop = useCallback(() => {}, [editor]);

  return {
    rInput,
    handleFocus: noop,
    handleBlur: noop,
    handleKeyDown,
    handleChange,
    handleInputPointerDown,
    handleDoubleClick: stopEventPropagation,
    isEmpty: text.trim().length === 0,
    isEditing,
    isEditingAnything,
  };
}

export function getLegacyOffsetX(
  align: TLDefaultHorizontalAlignStyle | string,
  padding: number,
  spans: { text: string; box: BoxModel }[],
  totalWidth: number
): number | undefined {
  if ((align === "start-legacy" || align === "end-legacy") && spans.length !== 0) {
    const spansBounds = Box.From(spans[0].box);
    for (const { box } of spans) {
      spansBounds.union(box);
    }
    if (align === "start-legacy") {
      return (totalWidth - 2 * padding - spansBounds.width) / 2;
    } else if (align === "end-legacy") {
      return -(totalWidth - 2 * padding - spansBounds.width) / 2;
    }
  }
}

// sneaky TLDefaultHorizontalAlignStyle for legacies
export function isLegacyAlign(align: TLDefaultHorizontalAlignStyle | string): boolean {
  return align === "start-legacy" || align === "middle-legacy" || align === "end-legacy";
}

const fixNewLines = /\r?\n|\r/g;
function normalizeTextForDom(text: string) {
  return text
    .replace(fixNewLines, "\n")
    .split("\n")
    .map((x) => x || " ")
    .join("\n");
}

export function measureTextWithLetterSpacing(
  editor: Editor,
  textToMeasure: string,
  opts: {
    fontStyle: string;
    fontWeight: string;
    fontFamily: string;
    fontSize: number;
    lineHeight: number;
    maxWidth: null | number;
    minWidth?: null | number;
    padding: string;
    disableOverflowWrapBreaking?: boolean;
    letterSpacing: number;
    strokeWidth: number;
  }
): BoxModel & { scrollWidth: number } {
  const elm = document.createElement("div");
  elm.classList.add("tl-text", "tl-text-measure");
  elm.tabIndex = -1;
  editor.getContainer().appendChild(elm);

  elm.setAttribute("dir", "auto");
  elm.style.setProperty("unicode-bidi", "plaintext");
  elm.style.setProperty("font-family", opts.fontFamily);
  elm.style.setProperty("font-style", opts.fontStyle);
  elm.style.setProperty("font-weight", opts.fontWeight);
  elm.style.setProperty("font-size", opts.fontSize + "px");
  elm.style.setProperty(
    "line-height",
    opts.lineHeight === 0
      ? "0px"
      : opts.lineHeight >= 1
      ? opts.lineHeight * opts.fontSize + "px"
      : opts.lineHeight * opts.fontSize + "px"
  );
  elm.style.setProperty("max-width", opts.maxWidth === null ? null : opts.maxWidth + "px");
  elm.style.setProperty("min-width", opts.minWidth === null ? null : opts.minWidth + "px");
  elm.style.setProperty("padding", opts.padding);
  elm.style.setProperty("overflow-wrap", opts.disableOverflowWrapBreaking ? "normal" : "break-word");
  elm.style.setProperty("letter-spacing", opts.letterSpacing + "px");
  elm.style.setProperty("vertical-align", "middle");

  elm.textContent = normalizeTextForDom(textToMeasure);

  // Force a reflow to ensure fonts are applied before measuring
  elm.offsetHeight;

  const scrollWidth = elm.scrollWidth;
  const rect = elm.getBoundingClientRect();
  elm.remove();

  const height = opts.lineHeight === 0 ? 0 : rect.height + opts.strokeWidth;

  return {
    x: 0,
    y: 0,
    w: rect.width,
    h: height,
    scrollWidth,
  };
}

export function getTextSize(editor: Editor, props: CustomTextUtilType["props"]) {
  const { font, text, autoSize, size, w, lineHeight, letterSpacing, fontWeight = 400, italic, strokeWidth } = props;

  const minWidth = autoSize ? 16 : Math.max(16, w);
  const fontSize = size;

  const cw = autoSize ? Infinity : Math.floor(Math.max(minWidth, w));

  function getFontWeightString(fontWeight: number): string {
    // Normalize font weight to valid CSS values
    if (typeof fontWeight !== "number" || isNaN(fontWeight)) {
      return "400";
    }

    // Map common font weights to valid values
    if (fontWeight <= 350) return "300";
    if (fontWeight <= 450) return "400";
    if (fontWeight <= 650) return "600";
    return "700";
  }

  try {
    const fontFamily = `"${font}", -apple-system, BlinkMacSystemFont, sans-serif`;

    const result = measureTextWithLetterSpacing(editor, text, {
      fontStyle: italic || "normal",
      fontWeight: getFontWeightString(fontWeight),
      fontFamily: fontFamily,
      fontSize: fontSize,
      maxWidth: cw,
      lineHeight: lineHeight !== undefined ? lineHeight : 1,
      padding: "0px",
      letterSpacing: letterSpacing || 0,
      strokeWidth: strokeWidth ?? 0,
      disableOverflowWrapBreaking: autoSize, // Disable wrapping when autoSize is true
    });

    // For autoSize, use scrollWidth to get the full width of unwrapped text
    // For manual width, use rect.width which respects the maxWidth constraint
    const width = autoSize ? result.scrollWidth : result.w;

    if (autoSize) {
      // Add 1px padding for autoSize
      result.w = width + 1;
    }

    // Ensure minimum valid dimensions to prevent geometry errors
    const finalWidth = Math.max(16, (autoSize ? result.w : width) || minWidth);
    const height = Math.max(16, result.h || fontSize * (lineHeight ?? 1));

    return {
      width: finalWidth,
      height,
    };
  } catch (error) {
    console.warn("Error measuring text size, using fallback dimensions:", error);
    // Fallback dimensions to prevent geometry errors
    return {
      width: Math.max(16, minWidth),
      height: Math.max(16, fontSize * (lineHeight ?? 1)),
    };
  }
}

export function resizeScaled(
  shape: Extract<TLShape, { props: { scale: number } }> | CustomTextUtilType,
  {
    initialBounds,
    scaleX,
    scaleY,
    newPoint,
  }: {
    newPoint: VecModel;
    initialBounds: Box;
    scaleX: number;
    scaleY: number;
  }
) {
  // Separately determine scaleDelta for X and Y
  const scaleDeltaX = Math.abs(scaleX);
  const scaleDeltaY = Math.abs(scaleY);

  // If we're only scaling down, don't limit scaleDelta to a minimum value
  const scaleDelta = scaleX === 1 ? scaleDeltaY : Math.max(0.01, Math.min(scaleDeltaX, scaleDeltaY));

  // Calculate offset for negative scaling
  const offset = new Vec(0, 0);
  if (scaleX < 0) offset.x = -(initialBounds.width * scaleDelta);
  if (scaleY < 0) offset.y = -(initialBounds.height * scaleDelta);

  // Calculate new position
  const { x, y } = Vec.Add(newPoint, offset.rot(shape.rotation));

  return {
    x,
    y,
    props: {
      scale: scaleDelta * shape.props.scale!,
    },
  };
}

export function getFontDefForExport(fontStyle: string): SvgExportDef {
  return {
    key: `${DefaultFontStyle.id}:${fontStyle}`,
    async getElement(): Promise<React.ReactElement | null> {
      const font = findFont(fontStyle);
      if (!font) return null;

      const url: string = (font as any).$$_url;
      const fontFaceRule: string = (font as any).$$_fontface;
      if (!url || !fontFaceRule) return null;

      const fontFile = await (await fetch(url)).blob();
      const base64FontFile = await FileHelpers.blobToDataUrl(fontFile);

      const newFontFaceRule = fontFaceRule.replace(url, base64FontFile);
      //@ts-ignore
      return newFontFaceRule;
    },
  };
}

function findFont(name: string): FontFace | null {
  const fontFamily = name;
  // @ts-ignore
  for (const font of document.fonts) {
    if (fontFamily.includes(font.family)) {
      return font;
    }
  }
  return null;
}

export function useTextShapeKeydownHandler(id: TLShapeId, editor: Editor) {
  return useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (editor.getEditingShapeId() !== id) return;

      switch (e.key) {
        case "Enter": {
          if (e.ctrlKey || e.metaKey) {
            editor.complete();
          }
          break;
        }
        case "Tab": {
          preventDefault(e);
          if (e.shiftKey) {
            TextHelpers.unindent(e.currentTarget);
          } else {
            TextHelpers.indent(e.currentTarget);
          }
          break;
        }
      }
    },
    [editor, id]
  );
}

export function hexToRgba(color: string) {
  // Check if the color is a named color
  const ctx = document.createElement("canvas").getContext("2d");
  if (ctx) {
    ctx.fillStyle = color;
    color = ctx.fillStyle;
  }

  // If the color is in hex format
  if (color.startsWith("#")) {
    const bigint = parseInt(color.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    const a = ((bigint >> 24) & 255) / 255 || 1; // Extract alpha if present, default to 1
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }

  // If the color is in rgba format
  if (color.startsWith("rgba")) {
    return color.replace(/rgba\(([^)]+)\)/, `rgba($1)`);
  }

  // If the color is in rgb format
  if (color.startsWith("rgb")) {
    return color.replace(/rgb\(([^)]+)\)/, `rgba($1)`);
  }

  // If the color is in hsl format
  if (color.startsWith("hsl")) {
    const match = color.match(/hsl\(([^)]+)\)/);
    const hsl = match ? match[1].split(",") : [0, 0, 0];
    const h = parseInt(hsl[0].toString());
    const s = parseInt(hsl[1].toString());
    const l = parseInt(hsl[2].toString());
    const a = 1;
    return `hsla(${h}, ${s}%, ${l}%, ${a})`;
  }

  // Default to black if the color format is not recognized
  return `rgba(0, 0, 0, 1)`;
}
